/**
 * Trungnam MES PRO · 主入口
 *
 * 版本: 见 src/constants/company.ts APP_VERSION (单一事实源)
 *
 * 流程:
 *   1. 初始化 i18n (zh/vi)
 *   2. 初始化 DB (sql.js + IndexedDB 镜像)
 *   3. 启动 Router (hash-based · 兼容老 #salesOrderList 等)
 *   4. 隐藏 splash · 渲染 #app
 */

import { initI18n } from './core/i18n';
import { initDb } from './core/db';
import { initRouter } from './core/router';
import { hideSplash } from './widgets/Splash';
import { initErrorMonitoring } from './services/errorMonitoring';
import { initCommandPalette } from './widgets/CommandPalette';
import './services/keyBinds'; // v339 · 全局键盘快捷键 (auto-init)
import './services/tvMode'; // v340 · TV mode 大屏 (auto-init)
import { initAutoI18n } from './services/autoI18n'; // v341 · DOM 自动 ZH→VI 翻译
import { startPmScanner } from './services/pmEngine'; // v350 · PM 预防维修自动触发
import { initAutoRefresh } from './services/autoRefresh'; // v351 · 60s 自动刷新 (operator dashboard)
import { initCheatsheet } from './services/cheatsheet'; // v354 · ? 快捷键 cheatsheet
import { initModalGuard } from './services/modalGuard'; // v354 · 通用 modal ✕ 强插
import { initSearchSelectAuto } from './widgets/SearchSelect'; // v356 · select > 20 自动可搜
import { APP_VERSION } from './constants/company';
import './styles/tailwind.css'; // v348 · 替代 cdn.tailwindcss.com
import './styles/tokens.css';   // v351.1 hotfix · CSS variables + 工业 token (v310 起一直缺 import)
import './styles/mobile.css';

async function bootstrap() {
  try {
    // 1. i18n (并行 fetch zh.json + vi.json)
    await initI18n();

    // 2. DB 初始化 (sql.js + IndexedDB 镜像 + schema migration)
    await initDb();

    // 3. Router 启动 (hash → page module lazy load)
    await initRouter();

    // 4. 启动错误监控 (db 已 ready, 可写 audit_events)
    initErrorMonitoring();

    // 5. Cmd+K palette · 全局 ⌘K 命令面板 (复用 v200 TN_KEYBINDS)
    initCommandPalette();

    // 6. AutoI18n · DOM 自动 ZH→VI (v341)
    initAutoI18n();

    // 7. PM 预防维修自动触发 (v350 Sprint 9-A)
    startPmScanner();

    // 8. AutoRefresh 60s tick (v351 · operator dashboard 趋势)
    initAutoRefresh();

    // 9. v354 · 快捷键 cheatsheet (?键) + 通用 modal ✕ 强插
    initCheatsheet();
    initModalGuard();

    // v356 · select > 20 option 自动加搜索框 (tn-render-done event 驱动)
    initSearchSelectAuto();

    // 10. 隐藏 splash + 显 app
    hideSplash();

    // eslint-disable-next-line no-console
    console.log(`[v${APP_VERSION}] bootstrap OK`);
  } catch (err) {
    console.error(`[v${APP_VERSION}] bootstrap FAILED:`, err);
    // 友好降级 (不让用户看到 splash 卡死)
    const splash = document.getElementById('tn-boot-splash');
    if (splash) {
      const sub = splash.querySelector('.tn-boot-sub');
      if (sub) sub.textContent = '⚠️ 启动失败 · 请刷新重试 / Khởi động lỗi · vui lòng tải lại';
    }
  }
}

// DOM ready 后启动
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
